'use client'

import { useState } from 'react'
import { motion } from 'framer-motion'
import { useRouter } from 'next/navigation'
import Link from 'next/link'
import { UserPlus, Mail, Lock, User, Check, X, Target, Rocket, Users2, GraduationCap } from 'lucide-react'
import Image from 'next/image'
import { useBranding } from '@/lib/contexts/BrandingContext'

export default function SignupPage() {
  const { branding } = useBranding()
  const router = useRouter()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [fullName, setFullName] = useState('')
  const [role, setRole] = useState<'instructor' | 'client'>('client')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  // Password strength indicators
  const hasMinLength = password.length >= 8
  const hasUppercase = /[A-Z]/.test(password)
  const hasLowercase = /[a-z]/.test(password)
  const hasNumber = /[0-9]/.test(password)
  const hasSpecial = /[^A-Za-z0-9]/.test(password)
  const passwordsMatch = password === confirmPassword && password.length > 0

  const isPasswordValid = hasMinLength && hasUppercase && hasLowercase && hasNumber && hasSpecial

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    try {
      if (!isPasswordValid) {
        throw new Error('Please meet all password requirements')
      }

      if (!passwordsMatch) {
        throw new Error('Passwords do not match')
      }

      const response = await fetch('/api/auth/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          password,
          fullName,
          role,
        }),
      })

      const data = await response.json()

      if (!data.success) {
        // Show detailed validation errors if available
        if (data.error?.details && Array.isArray(data.error.details)) {
          const errorMessages = data.error.details
            .map((err: any) => `${err.field}: ${err.message}`)
            .join('\n')
          throw new Error(errorMessages || data.error?.message || 'Signup failed')
        }
        throw new Error(data.error?.message || 'Signup failed')
      }

      setSuccess(true)
      // Redirect to login after 3 seconds
      setTimeout(() => {
        router.push('/login')
      }, 3000)
    } catch (err: any) {
      setError(err.message || 'Signup failed. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  if (success) {
    return (
      <div className="min-h-screen hg-bg-primary flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="relative w-full max-w-md"
        >
          <div className="bg-[var(--hg-bg-card)] backdrop-blur-xl rounded-2xl border border-[var(--hg-border-color)] p-8 shadow-2xl text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4" style={{ backgroundColor: 'var(--hg-success-bg)' }}>
              <Check className="w-8 h-8" style={{ color: 'var(--hg-success)' }} />
            </div>
            <h1 className="text-2xl font-bold hg-text-primary mb-2 font-gendy">Account Created!</h1>
            <p className="hg-text-muted font-diatype mb-6">
              Please check your email to verify your account.
            </p>
            <p className="text-sm hg-text-muted font-diatype">
              Redirecting to login page...
            </p>
          </div>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="min-h-screen hg-bg-primary flex">
      {/* Left Side - Information Panel */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden" style={{ background: 'linear-gradient(to bottom right, var(--hg-cyan-bg), var(--hg-info-bg), var(--hg-cyan-bg))' }}>
        {/* Animated Background */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(139,92,246,0.15),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_60%,rgba(59,130,246,0.1),transparent_50%)]" />

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center px-16 py-20">
          {/* Logo */}
          <Link href="/" className="mb-12 inline-block">
            <Image
              src={branding?.logo?.url || '/hmn_logo.png'}
              alt={branding?.company_name || 'HMN'}
              width={200}
              height={50}
              className="h-10 w-auto"
              priority
            />
          </Link>

          {/* Main Heading */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-5xl font-bold hg-text-primary mb-6 font-gendy leading-tight">
              Start Your AI Transformation Journey
            </h1>
            <p className="text-xl hg-text-secondary mb-12 font-diatype leading-relaxed">
              Join thousands of organizations building AI maturity and transforming their teams with intelligent automation.
            </p>
          </motion.div>

          {/* Benefits */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-5"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'var(--hg-cyan-bg)' }}>
                <Target className="w-6 h-6" style={{ color: 'var(--hg-cyan-text)' }} />
              </div>
              <div>
                <h3 className="hg-text-primary font-semibold mb-1 font-gendy">Personalized AI Assessment</h3>
                <p className="hg-text-muted text-sm font-diatype">Discover your organization's AI maturity level and get a custom roadmap</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'var(--hg-info-bg)' }}>
                <Rocket className="w-6 h-6" style={{ color: 'var(--hg-info)' }} />
              </div>
              <div>
                <h3 className="hg-text-primary font-semibold mb-1 font-gendy">Ready-to-Deploy Workflows</h3>
                <p className="hg-text-muted text-sm font-diatype">Access 50+ AI automation workflows built for real business needs</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'var(--hg-cyan-bg)' }}>
                <Users2 className="w-6 h-6" style={{ color: 'var(--hg-cyan-text)' }} />
              </div>
              <div>
                <h3 className="hg-text-primary font-semibold mb-1 font-gendy">Expert Community</h3>
                <p className="hg-text-muted text-sm font-diatype">Connect with AI transformation leaders and industry experts</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'var(--hg-success-bg)' }}>
                <GraduationCap className="w-6 h-6" style={{ color: 'var(--hg-success)' }} />
              </div>
              <div>
                <h3 className="hg-text-primary font-semibold mb-1 font-gendy">Live Training & Workshops</h3>
                <p className="hg-text-muted text-sm font-diatype">Interactive sessions with hands-on AI implementation guidance</p>
              </div>
            </div>
          </motion.div>

          {/* Trust Indicators */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-12 pt-8 border-t border-[var(--hg-border-color)]"
          >
            <p className="text-sm hg-text-muted mb-4 font-diatype">Trusted by leading organizations</p>
            <div className="flex items-center gap-8 hg-text-muted">
              <div className="text-2xl font-bold font-gendy">500+</div>
              <div className="text-2xl font-bold font-gendy">10K+</div>
              <div className="text-2xl font-bold font-gendy">95%</div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Right Side - Signup Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 sm:px-12 lg:px-16 py-12 overflow-y-auto">
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-md"
        >
          {/* Mobile Logo */}
          <Link href="/" className="lg:hidden mb-8 inline-block">
            <Image
              src={branding?.logo?.url || '/hmn_logo.png'}
              alt={branding?.company_name || 'HMN'}
              width={180}
              height={50}
              className="h-10 w-auto"
            />
          </Link>

          {/* Header */}
          <div className="mb-8">
            <h2 className="text-3xl font-bold hg-text-primary mb-2 font-gendy">Create Account</h2>
            <p className="hg-text-muted font-diatype">Begin your AI transformation journey today</p>
          </div>

          {/* Error Message */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 rounded-xl text-sm font-diatype whitespace-pre-line"
              style={{ backgroundColor: 'var(--hg-danger-bg, rgba(239,68,68,0.1))', border: '1px solid var(--hg-danger-border, rgba(239,68,68,0.2))', color: 'var(--hg-danger)' }}
            >
              {error}
            </motion.div>
          )}

          {/* Form */}
          <form onSubmit={handleSignup} className="space-y-5">
            {/* Full Name */}
            <div>
              <label className="block text-sm font-medium hg-text-secondary mb-2 font-diatype">
                Full Name
              </label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 hg-text-muted" />
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="John Doe"
                  required
                  autoComplete="name"
                  className="w-full pl-12 pr-4 py-3.5 bg-[var(--hg-bg-hover)] border border-[var(--hg-border-color)] rounded-xl hg-text-primary placeholder-[var(--hg-text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--hg-cyan-500)] focus:border-[var(--hg-cyan-500)] transition-all font-diatype"
                  style={{ fontSize: '16px' }}
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium hg-text-secondary mb-2 font-diatype">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 hg-text-muted" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  required
                  autoComplete="email"
                  className="w-full pl-12 pr-4 py-3.5 bg-[var(--hg-bg-hover)] border border-[var(--hg-border-color)] rounded-xl hg-text-primary placeholder-[var(--hg-text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--hg-cyan-500)] focus:border-[var(--hg-cyan-500)] transition-all font-diatype"
                  style={{ fontSize: '16px' }}
                />
              </div>
            </div>

            {/* Role Selection */}
            <div>
              <label className="block text-sm font-medium hg-text-secondary mb-2 font-diatype">
                I am a...
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setRole('client')}
                  className={`py-3 px-4 rounded-xl border transition-all font-diatype ${
                    role === 'client'
                      ? 'hg-text-primary'
                      : 'bg-[var(--hg-bg-hover)] border-[var(--hg-border-color)] hg-text-muted hover:bg-[var(--hg-bg-card)]'
                  }`}
                  style={role === 'client' ? { backgroundColor: 'var(--hg-cyan-bg)', borderColor: 'var(--hg-cyan-border)' } : undefined}
                >
                  Client
                </button>
                <button
                  type="button"
                  onClick={() => setRole('instructor')}
                  className={`py-3 px-4 rounded-xl border transition-all font-diatype ${
                    role === 'instructor'
                      ? 'hg-text-primary'
                      : 'bg-[var(--hg-bg-hover)] border-[var(--hg-border-color)] hg-text-muted hover:bg-[var(--hg-bg-card)]'
                  }`}
                  style={role === 'instructor' ? { backgroundColor: 'var(--hg-cyan-bg)', borderColor: 'var(--hg-cyan-border)' } : undefined}
                >
                  Instructor
                </button>
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium hg-text-secondary mb-2 font-diatype">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 hg-text-muted" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  autoComplete="new-password"
                  className="w-full pl-12 pr-4 py-3.5 bg-[var(--hg-bg-hover)] border border-[var(--hg-border-color)] rounded-xl hg-text-primary placeholder-[var(--hg-text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--hg-cyan-500)] focus:border-[var(--hg-cyan-500)] transition-all font-diatype"
                  style={{ fontSize: '16px' }}
                />
              </div>

              {/* Password Requirements */}
              {password && (
                <div className="mt-3 space-y-1.5">
                  <PasswordRequirement met={hasMinLength} text="At least 8 characters" />
                  <PasswordRequirement met={hasUppercase} text="One uppercase letter" />
                  <PasswordRequirement met={hasLowercase} text="One lowercase letter" />
                  <PasswordRequirement met={hasNumber} text="One number" />
                  <PasswordRequirement met={hasSpecial} text="One special character (!@#$%^&*)" />
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-medium hg-text-secondary mb-2 font-diatype">
                Confirm Password
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 hg-text-muted" />
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  autoComplete="new-password"
                  className="w-full pl-12 pr-4 py-3.5 bg-[var(--hg-bg-hover)] border border-[var(--hg-border-color)] rounded-xl hg-text-primary placeholder-[var(--hg-text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--hg-cyan-500)] focus:border-[var(--hg-cyan-500)] transition-all font-diatype"
                  style={{ fontSize: '16px' }}
                />
              </div>
              {confirmPassword && (
                <div className="mt-2">
                  <PasswordRequirement met={passwordsMatch} text="Passwords match" />
                </div>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading || !isPasswordValid || !passwordsMatch}
              className="w-full py-4 btn-primary font-semibold rounded-xl flex items-center justify-center gap-2 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed font-diatype"
            >
              {isLoading ? (
                <>
                  <div className="w-5 h-5 border-2 rounded-full animate-spin" style={{ borderColor: 'rgba(255,255,255,0.3)', borderTopColor: 'var(--hg-text-inverse, #fff)' }} />
                  Creating account...
                </>
              ) : (
                <>
                  <UserPlus className="w-5 h-5" />
                  Create Account
                </>
              )}
            </button>
          </form>

          {/* Login Link */}
          <div className="mt-8 text-center">
            <p className="hg-text-muted text-sm font-diatype">
              Already have an account?{' '}
              <Link href="/login" className="font-semibold" style={{ color: 'var(--hg-cyan-text)' }}>
                Sign in →
              </Link>
            </p>
          </div>

          {/* Back to Home */}
          <div className="mt-6 text-center">
            <Link
              href="/"
              className="inline-flex items-center gap-2 text-sm hg-text-muted transition-colors font-diatype"
            >
              ← Back to Home
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  )
}

function PasswordRequirement({ met, text }: { met: boolean; text: string }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      {met ? (
        <Check className="w-3.5 h-3.5" style={{ color: 'var(--hg-success)' }} />
      ) : (
        <X className="w-3.5 h-3.5 hg-text-muted" />
      )}
      <span className="font-diatype" style={{ color: met ? 'var(--hg-success)' : 'var(--hg-text-muted)' }}>
        {text}
      </span>
    </div>
  )
}
